'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import SavedShipments from './SavedShipments';
import QuoteHistory from './QuoteHistory';
import OrderTracking from './OrderTracking';
import AccountSettings from './AccountSettings';
import PaymentMethods from './PaymentMethods';
import RealtimeNotifications from '@/components/common/RealtimeNotifications';
import { createClient } from '@/lib/supabase/client';

type TabType = 'shipments' | 'quotes' | 'tracking' | 'payments' | 'settings';

interface Tab {
  id: TabType;
  label: string;
  icon: string;
}

const tabs: Tab[] = [
  { id: 'shipments', label: 'Saved Shipments', icon: 'ArchiveBoxIcon' },
  { id: 'quotes', label: 'Quote History', icon: 'DocumentTextIcon' },
  { id: 'tracking', label: 'Order Tracking', icon: 'MapPinIcon' },
  { id: 'payments', label: 'Payment Methods', icon: 'CreditCardIcon' },
  { id: 'settings', label: 'Account Settings', icon: 'Cog6ToothIcon' },
];

export default function ProfileDashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('shipments');
  const supabase = createClient();
  const [userId, setUserId] = useState<string | null>(null);

  // Get authenticated user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
    };
    getUser();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Tab Navigation */}
      <div className="bg-card rounded-xl shadow-card border border-border mb-8">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap border-b-2 ${
                activeTab === tab.id
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab.icon} size={20} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Real-time Notifications */}
      {userId && (
        <div className="mb-8">
          <RealtimeNotifications userId={userId} maxDisplay={5} />
        </div>
      )}

      {/* Tab Content */}
      <div className="animate-fade-in">
        {activeTab === 'shipments' && <SavedShipments />}
        {activeTab === 'quotes' && <QuoteHistory />}
        {activeTab === 'tracking' && <OrderTracking />}
        {activeTab === 'payments' && <PaymentMethods />}
        {activeTab === 'settings' && <AccountSettings />}
      </div>
    </div>
  );
}